import { type ManualOrderChangelogDisplay } from "@redotech/redo-merchant-inventory-app/intelligent-inventory/components/changelog-card";
import * as z from "zod";
import { type RpcWriteProposal } from "./confirmation-data";

/** RPC name of the fulfillment toolset's manual-order write. */
export const CREATE_MANUAL_FULFILLMENT_GROUPS = "createManualFulfillmentGroups";

const displayAddressSchema = z.object({
  name: z.string().nullish(),
  company: z.string().nullish(),
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  province: z.string().nullish(),
  zip: z.string().nullish(),
  countryCode: z.string().nullish(),
});

const displayItemSchema = z.object({
  title: z.string().nullish(),
  productTitle: z.string().nullish(),
  sku: z.string().nullish(),
  quantity: z.number(),
});

/** Display-only view of the createManualFulfillmentGroups input; unknown keys
 *  are stripped, so the full RPC input always satisfies this. */
const manualOrderWriteInputSchema = z.object({
  fulfillmentOrders: z.array(
    z.object({
      location: displayAddressSchema.nullish(),
      address: displayAddressSchema,
      orderInfo: z.object({
        orderNumber: z.string(),
        items: z.array(displayItemSchema),
      }),
      customer: z
        .object({ email: z.string().nullish(), name: z.string().nullish() })
        .nullish(),
    }),
  ),
});

function formatAddress(address: z.infer<typeof displayAddressSchema>): string {
  const parts = [
    address.name,
    address.company,
    address.address1,
    address.address2,
    [address.city, address.province, address.zip].filter(Boolean).join(" "),
    address.countryCode,
  ].filter((part): part is string => Boolean(part && part.trim()));
  return parts.join(", ");
}

/** A manual-order write rendered as inventory-style changelog entries. */
export interface ManualOrderChangelogEntry {
  action: "createManualOrder";
  manualOrder: ManualOrderChangelogDisplay;
}

/** One ChangelogCard entry per fulfillment order, or undefined when a write
 *  doesn't parse — the caller falls back to the generic confirmation card. */
export function manualOrderWritesToChangelogEntries(
  writes: RpcWriteProposal[],
): ManualOrderChangelogEntry[] | undefined {
  const entries: ManualOrderChangelogEntry[] = [];
  for (const write of writes) {
    const parsed = manualOrderWriteInputSchema.safeParse(write.input);
    if (!parsed.success) {
      return undefined;
    }
    for (const order of parsed.data.fulfillmentOrders) {
      const customerParts = [
        order.customer?.name,
        order.customer?.email,
      ].filter(Boolean);
      entries.push({
        action: "createManualOrder",
        manualOrder: {
          orderNumber: order.orderInfo.orderNumber,
          shipFrom: order.location
            ? formatAddress(order.location)
            : "Default location",
          shipTo: formatAddress(order.address),
          customer: customerParts.length
            ? customerParts.join(" — ")
            : undefined,
          items: order.orderInfo.items.map((item) => ({
            title:
              item.title ?? item.productTitle ?? item.sku ?? "Unknown product",
            sku: item.sku ?? undefined,
            quantity: item.quantity,
          })),
        },
      });
    }
  }
  return entries;
}
